import type { ReactNode } from "react";

import { Button } from "./Button";
import { StatusBadge, type StatusTone } from "./StatusBadge";

export interface ChipRegistrationStatusCardProps {
  /** Already-resolved flow label, e.g. "Aanvoer" / "Afvoer" (i18n stays in the host). */
  flowLabel: string;
  /** Already-resolved status label, e.g. "Geregistreerd". */
  statusLabel: string;
  /** Badge tone for the status (success / neutral / warning / danger). */
  statusTone?: StatusTone;
  /** Optional meta line — the host composes it (attempts · date · registry reference …). */
  meta?: ReactNode;
  /** Already-resolved "last error" label (e.g. "Laatste fout"); only shown when `error` is set. */
  errorLabel?: string;
  /** The last-error detail; the error row renders only when this is non-empty. */
  error?: string | null;
  /** Re-queue handler — when provided, a secondary action button is rendered with `requeueLabel`. */
  onRequeue?: () => void;
  /** Already-resolved re-queue button label (e.g. "Registratie opnieuw versturen"). */
  requeueLabel?: string;
  /** Disables the re-queue button (e.g. while a request is in flight). */
  requeueDisabled?: boolean;
}

/**
 * The presentational chip-registration status card — one registration's flow, a status
 * {@link StatusBadge}, an optional meta line, an optional last-error row, and an optional
 * "re-queue" action. The reusable unit behind the per-animal status surface and (by design) the
 * intake/release wizards' post-finish verdict; the host fetches the data and resolves all strings
 * (so @fawkes/ui stays free of the app's i18n + data runtime). Styles live in ui.css, token-driven.
 */
export function ChipRegistrationStatusCard({
  flowLabel,
  statusLabel,
  statusTone = "neutral",
  meta,
  errorLabel,
  error,
  onRequeue,
  requeueLabel,
  requeueDisabled,
}: ChipRegistrationStatusCardProps) {
  return (
    <div className="fui-chipreg-card">
      <div className="fui-chipreg-card__head">
        <span className="fui-chipreg-card__flow">{flowLabel}</span>
        <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>
      </div>
      {meta != null && <p className="fui-chipreg-card__meta">{meta}</p>}
      {error ? (
        <p className="fui-chipreg-card__error">
          {errorLabel ? <span className="fui-chipreg-card__error-label">{errorLabel}: </span> : null}
          {error}
        </p>
      ) : null}
      {onRequeue ? (
        <div className="fui-chipreg-card__actions">
          <Button variant="secondary" onClick={onRequeue} disabled={requeueDisabled}>
            {requeueLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
